import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";

import { client, apiEndpoints, entitiesEnabled } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import { serverUrl } from "../../config";
import { decodeHtmlEntities } from "../../utils/helpers";

import { Typography, Button } from "@mui/material";
import ChevronRight from '@mui/icons-material/ChevronRightOutlined';
import { CircularProgress } from "@mui/material";
import ClearIcon from '@mui/icons-material/ClearOutlined';
import { Row, Col } from "react-bootstrap";

export default function SearchItem(props) {

  const [uri, setUri] = useState("");
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const user = useSelector((state) => state).authReducer.user;

  const isDocument = ['documents', 'Documents', 'document', 'Document'].includes(props.item.type);
  const isLink = ['external_resource', 'External Resource', 'link', 'Link'].includes(props.item.type);

  useEffect(() => {
    // Custom-entity (v3) app: documents open via their /documents/view detail
    // screen on click, so there is nothing to pre-fetch. Links have no detail
    // route, so their target URL is still resolved up-front to open on click.
    if (entitiesEnabled) {
      if (isLink) {
        setIsLoading(true);
        fetchLinkV3(props.item.id ?? props.item.nid);
      }
      return;
    }

    // Legacy node (v2) app.
    if (isDocument) {
      setIsLoading(true);
      fetchDocument(props.item.id ?? props.item.nid);
    }
    if (isLink) {
      setIsLoading(true);
      fetchLink(props.item.id ?? props.item.nid);
    }
    // eslint-disable-next-line
  }, [props.item]);

  const fetchDocument = (nid) => {
    let path = `${apiEndpoints.documents}/all/${nid}`;
    getOnlineNode(path, user.access_token)
      .then((response) => {
        setIsLoading(false);

        if (response?.data?.rows[0]?.uri) {
          setUri(response?.data?.rows[0]?.uri);
        }

        if (response?.data?.rows[0]?.field_document) {
          setUri(response?.data?.rows[0]?.field_document);
        }
      })
      .catch((_error) => {
        setIsLoading(false);
        setError(true);
      });
  }

  const fetchLink = (nid) => {
    let path = `${apiEndpoints.links}/all/${nid}`;
    getOnlineNode(path, user.access_token)
      .then((response) => {
        if (response?.data?.rows[0]?.field_url) {
          setUri(response?.data?.rows[0]?.field_url);
        }

        if (response?.data?.rows[0]?.url) {
          setUri(response?.data?.rows[0]?.url);
        }

        setIsLoading(false);

      })
      .catch((_error) => {
        setIsLoading(false);
        setError(true);
      });
  }

  // v3 link entity: resolve the outbound URL from the collection endpoint
  // (?id=), mirroring how the links screen opens `field_website` / `url`.
  const fetchLinkV3 = (id) => {
    let path = `${apiEndpoints.links}?id=${id}&_format=json`;
    getOnlineNode(path, user.access_token)
      .then((response) => {
        const row = response?.data?.rows?.[0];
        if (row?.field_website) {
          setUri(row.field_website);
        } else if (row?.url) {
          setUri(row.url);
        }
        setIsLoading(false);
      })
      .catch((_error) => {
        setIsLoading(false);
        setError(true);
      });
  }


  const handleLink = () => {
    switch (props.item.type) {
      case "News": {
        navigate(`/news/view/${props.item.id ?? props.item.nid}`);
        break;
      }
      case "Event":
      case "Events": {
        navigate(`/events/view/${props.item.id ?? props.item.nid}`);
        break;
      }
      case "Video":
      case "Videos": {
        navigate(`/videos/view/${props.item.id ?? props.item.nid}`);
        break;
      }
      case "Podcast":
      case "Podcasts": {
        navigate(`/podcasts/view/${props.item.id ?? props.item.nid}`);
        break;
      }
      case "Gallery":
        navigate(`/galleries/view/${props.item.id ?? props.item.nid}`);
        break;
      case "Course": {
        const trainingId = client === "equans" ? props.item.nid : (props.item.id || props.item.nid);
        navigate(`/training/view/${trainingId}`);
        break;
      }
      case "Quiz": {
        navigate(`/quizzes/view/${props.item.id ?? props.item.nid}`);
        break;
      }
      case "Document":
      case "Documents": {
        if (entitiesEnabled) {
          navigate(`/documents/view/${props.item.id ?? props.item.nid}`);
        } else if (uri) {
          window.open(serverUrl + uri, "_blank");
        }
        break;
      }
      case "Link":
      case "External Resource":
        window.open(uri, "_blank");
        break;
      case "Company Directory":
        navigate(`/company_directory/view/${props.item.id ?? props.item.nid}`);
        break;
      case "Webform": {
        props.showForm({ title: props.item.title, id: props.item.id ?? props.item.nid });
        break;
      }
      default:
        return;
    }
  }

  const iconSize = 20;

  return (
    <Button
      className="w-100 d-flex justify-content-between align-items-center my-1"
      variant="searchItem"
      owner="client"
      lineHeight="wide"
      onClick={() => handleLink()}
      endIcon={
        error ?
          <ClearIcon
            sx={{ fontSize: `${iconSize}px` }}
          /> :
          isLoading ?
            <CircularProgress
              color="clientPrimary"
              size={iconSize}
            /> :
            <ChevronRight
              sx={{ fontSize: `${iconSize}px` }}
            />
      }
    >
      <Row className="w-100">
        <Col xs={10} sm={8} md={10}>
          <Typography
            family="secondary"
            className="text-start"
            owner="client"
            variant="subtitle1"
            lh="semi-wide"
            weight="medium"
          >
            {decodeHtmlEntities(props.item.title)}
          </Typography>
        </Col>
      </Row>
    </Button>
  )
}